import { initialsAvatarDataUrl } from "@compass/shared/utils/initialsAvatar";
import clsx from "clsx";
import { useState } from "react";

type AvatarType = {
    photoUrl?: string | null;
    className?: string;
    /** Display name — used to derive initials when `initials` is not provided. */
    name?: string | null;
    /**
     * Server-computed initials (1–2 chars). Preferred over `name` when present
     * because it's consistent with what the server returns on the author DTO.
     */
    initials?: string;
    /** Stable seed used to pick a color from the palette. Defaults to initials. */
    seed?: string | number;
};

const deriveInitials = (name: AvatarType["name"]): string => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    const first = parts[0]!.charAt(0).toUpperCase();
    const last = parts.length > 1 ? parts[parts.length - 1]!.charAt(0).toUpperCase() : "";
    return first + last;
};

export const Avatar = ({ className, photoUrl, name, initials, seed }: AvatarType) => {
    const [imageLoaded, setImageLoaded] = useState(false);

    const resolvedInitials = initials ?? deriveInitials(name);
    const fallbackSrc = initialsAvatarDataUrl({
        initials: resolvedInitials,
        seed: seed ?? resolvedInitials,
    });

    return (
        <div className={clsx("flex items-center justify-center rounded-full", className)}>
            {photoUrl ? (
                <>
                    {imageLoaded ? (
                        <img
                            className="h-full w-full rounded-xl object-cover"
                            src={photoUrl}
                            alt="User avatar"
                            onError={() => setImageLoaded(false)}
                        />
                    ) : (
                        <img
                            className="h-full w-full rounded-xl object-cover"
                            src={fallbackSrc}
                            alt={resolvedInitials}
                        />
                    )}
                    {/* Hidden image to detect loading */}
                    <img
                        src={photoUrl}
                        alt=""
                        style={{ display: "none" }}
                        onLoad={() => setImageLoaded(true)}
                        onError={() => setImageLoaded(false)}
                    />
                </>
            ) : (
                <img
                    className="h-full w-full rounded-xl object-cover"
                    src={fallbackSrc}
                    alt={resolvedInitials}
                />
            )}
        </div>
    );
};
